import { faChevronDown, faPowerOff } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  AccordionSummary,
  Button,
  Card,
  CircularProgress,
  InputLabel,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import Grid from '@mui/material/Grid';
import dayjs from 'dayjs';
import { orderBy } from 'lodash';

import DeviceStatusChip from '@/components/DeviceStatusChip';
import { YEAR_MONTH_DAY_TIME_FORMAT } from '@/constants/date.constants';
import { useStyles } from '@/features/DevicesTable';
import SOURCE_TYPE from '@/features/SourceCreate/SourceCreate.constants';
import { useSourceDialog } from '@/features/SourceCreate/SourceCreate.hooks';
import type { ResubmitCustomObjectRequest } from '@/features/SourceCreate/SourceCreate.types';

import {
  calculateStatusFromStatusCode,
  humanizeTime
} from '../../Devices.service';
import { Accordion, AccordionDetails } from './Accordion.styled';
import type { SourceStatusCardProps } from './SourceStatusCard.types';

const EXPAND_ICON_STYLE = { fontSize: '0.9rem' } as const;
const EMPTY_RIGHT_BADGE_STYLE = { position: 'absolute', right: '0' } as const;
const EMPTY_RIGHT_BADGE_NUMERIC_STYLE = {
  position: 'absolute',
  right: 0
} as const;
const DETAIL_VALUE_STYLE = {
  flex: 0.6,
  inlineSize: '10.625rem',
  wordWrap: 'break-word'
} as const;

type SourceStatusItem = NonNullable<
  SourceStatusCardProps['item']['log_type_statuses']
>[number];
type FailedCategoryItem = NonNullable<
  SourceStatusCardProps['item']['failed_categories']
>[number];
type CustomObjectStatusItem = NonNullable<
  SourceStatusCardProps['item']['custom_object_status']
>[number];
type EventAvailabilityItem = NonNullable<
  SourceStatusCardProps['item']['events_availability_history']
>[number];

/**
 * Renders a source status card, including source-specific status details.
 *
 * @param props Component properties.
 * @returns Source status card.
 */
export const SourceStatusCard = (props: SourceStatusCardProps) => {
  const { item } = props;
  const { resubmitCustomObjectMutation } = useSourceDialog();
  // Sorting by status code in descending order to get the highest order of status code
  const sourceStatuses = orderBy(
    item?.source_statuses || [],
    'status_code',
    'desc'
  );

  const status_code = sourceStatuses?.[0]?.status_code?.toString() ?? '';
  const statusText = calculateStatusFromStatusCode(`${status_code}`);
  const label = sourceStatuses?.[0]?.status_code_detail?.status_label;

  const { classes } = useStyles();

  /**
   * Converts UTC epoch milliseconds into local display format.
   *
   * @param milliseconds UTC epoch milliseconds.
   * @returns Localized date-time string.
   */
  const convertToTimestamp = (milliseconds: number): string => {
    return dayjs.utc(milliseconds).local().format(YEAR_MONTH_DAY_TIME_FORMAT); // Returns the date-time in the local timezone
  };

  const handleResubmitCustomObject = (
    source_uid: string,
    custom_object: string
  ) => {
    resubmitCustomObjectMutation.mutate({
      source_uid,
      custom_object
    } as ResubmitCustomObjectRequest);
  };

  const hasSalesForceStatus =
    Array.isArray(item?.custom_object_status) &&
    item.custom_object_status.length > 0 &&
    item?.source_type_id === SOURCE_TYPE.SALESFORCE;

  const hasWarningStatus =
    hasSalesForceStatus &&
    Array.isArray(item?.custom_object_status) &&
    item?.custom_object_status.some(({ status }: CustomObjectStatusItem) =>
      ['failed', 'ready'].includes(status?.toLowerCase())
    );

  const getCustomObjectStatusPresentation = (status?: string) => {
    const statusLower = status?.toLowerCase();
    const isFailed = statusLower === 'failed';
    const isRunning = statusLower === 'running';
    const isReady = statusLower === 'ready';

    return {
      isFailed,
      displayStatus: isReady ? 'Request Initiated' : status,
      statusClass: isFailed
        ? classes.errorColor
        : isRunning
          ? classes.successColor
          : classes.warningColor,
      actionIconClass: isFailed ? classes.successColor : classes.disabledColor
    };
  };

  /**
   * Builds common accordion summary markup used by status sections.
   *
   * @param title Summary title.
   * @param color Optional typography color.
   * @returns Accordion summary element.
   */
  const renderAccordionSummary = (
    title: string,
    color?: 'primary' | 'warning'
  ) => (
    <AccordionSummary
      expandIcon={
        <FontAwesomeIcon icon={faChevronDown} style={EXPAND_ICON_STYLE} />
      }
      aria-controls="status-info"
      id="status-info-header"
    >
      <Typography
        sx={{ fontWeight: 700 }}
        variant="body1"
        color={color}
        component="span"
      >
        {title}
      </Typography>
    </AccordionSummary>
  );

  return (
    <Card variant="outlined" sx={{ px: 2, py: 2 }}>
      <Stack
        direction="row"
        sx={{ alignItems: 'center', justifyContent: 'space-between' }}
        spacing={1}
      >
        <DeviceStatusChip status={statusText} />
        <Typography variant="body2" color="textSecondary">
          {humanizeTime(sourceStatuses?.[0]?.status_updated_at || '', true)}
        </Typography>
      </Stack>
      <Typography
        sx={{ fontWeight: 'bold', py: 1 }}
        variant="body1"
        component="div"
      >
        <span>
          {status_code} - {label}
        </span>
      </Typography>

      {/* Status Information */}
      {sourceStatuses?.[0]?.status_code !== 1800 && (
        <Accordion className={classes.statusInfoAccordion}>
          {renderAccordionSummary('Status Information', 'primary')}
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                <InputLabel id="process_start_time">
                  Process Start Time
                </InputLabel>
                <Typography>
                  {sourceStatuses?.[0]?.process_details?.process_start_time &&
                  sourceStatuses?.[0]?.process_details.process_start_time > 0
                    ? convertToTimestamp(
                        sourceStatuses?.[0]?.process_details?.process_start_time
                      )
                    : '--'}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                <InputLabel id="process_end_time">Process End Time</InputLabel>
                <Typography>
                  {sourceStatuses?.[0]?.process_details?.process_end_time &&
                  sourceStatuses?.[0]?.process_details.process_end_time > 0
                    ? convertToTimestamp(
                        sourceStatuses?.[0]?.process_details?.process_end_time
                      )
                    : '--'}
                </Typography>
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                <InputLabel id="source_failure_cnt">Failure Count</InputLabel>
                <Typography>
                  {sourceStatuses?.[0]?.process_details?.failure_count}
                </Typography>
              </Grid>

              {sourceStatuses?.[0]?.process_details?.error_message && (
                <Grid size={{ xs: 12, sm: 12, md: 12 }}>
                  <InputLabel id="source_error_msg">Error Message</InputLabel>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    aria-labelledby="source_error_msg"
                    name="source_error_msg"
                    variant="outlined"
                    value={sourceStatuses?.[0]?.process_details?.error_message}
                  />
                </Grid>
              )}
              {sourceStatuses?.[0]?.process_details?.error_details && (
                <Grid size={{ xs: 12, sm: 12, md: 12 }}>
                  <InputLabel id="source_error_trace">Error Trace</InputLabel>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    aria-labelledby="source_error_trace"
                    name="source_error_trace"
                    variant="outlined"
                    value={sourceStatuses?.[0]?.process_details?.error_details}
                  />
                </Grid>
              )}
            </Grid>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Failed Categories */}
      {item?.failed_categories &&
        item?.failed_categories.length > 0 &&
        item?.source_type_id !== SOURCE_TYPE.MEDIGATE &&
        item?.source_type_id !== SOURCE_TYPE.SALESFORCE && (
          <Accordion className={classes.statusInfoAccordion}>
            {renderAccordionSummary('Failed Categories', 'warning')}
            <AccordionDetails>
              {item?.failed_categories?.map(
                (logTypeStatus: FailedCategoryItem) => (
                  <Card
                    key={`failed-category-${logTypeStatus.log_type}-${logTypeStatus.last_modified_time}`}
                    variant="outlined"
                    sx={{ px: 2, py: 2, my: 1 }}
                  >
                    <div style={{ position: 'relative' }}>
                      <span style={EMPTY_RIGHT_BADGE_STYLE} />
                      <div className={classes.KVFlex}>
                        <div className={classes.flexLeftCol}>Category</div>
                        <div style={DETAIL_VALUE_STYLE}>
                          {logTypeStatus.log_type}
                        </div>
                      </div>
                      <div className={classes.KVFlex}>
                        <div className={classes.flexLeftCol}>
                          Last Updated Time
                        </div>
                        <div style={DETAIL_VALUE_STYLE}>
                          {dayjs
                            .utc(logTypeStatus?.last_modified_time)
                            .local()
                            .format(YEAR_MONTH_DAY_TIME_FORMAT)}
                        </div>
                      </div>
                      <Stack>
                        <Stack>Error Message</Stack>
                        <Stack className={classes.sourceErrorMessage}>
                          <div>{logTypeStatus?.error_message}</div>
                        </Stack>
                      </Stack>
                    </div>
                  </Card>
                )
              )}
            </AccordionDetails>
          </Accordion>
        )}

      {/* Vulnerability Scan Status */}
      {item?.log_type_statuses &&
        item?.log_type_statuses.length > 0 &&
        item?.source_type_id === SOURCE_TYPE.MEDIGATE && (
          <Accordion className={classes.statusInfoAccordion}>
            {renderAccordionSummary('Vulnerability Scan Status')}
            <AccordionDetails>
              {item?.log_type_statuses.map(
                (logTypeStatus: SourceStatusItem) => (
                  <Card
                    key={`log-type-status-${logTypeStatus.log_type}-${logTypeStatus.last_modified_time}`}
                    variant="outlined"
                    sx={{ px: 2, py: 2, my: 1 }}
                  >
                    <div style={{ position: 'relative' }}>
                      <span style={EMPTY_RIGHT_BADGE_STYLE} />
                      <div className={classes.KVFlex}>
                        <div className={classes.flexLeftCol}>Scan Type</div>
                        <div style={DETAIL_VALUE_STYLE}>
                          {logTypeStatus.log_type.toLowerCase() ===
                          'vulnerability'
                            ? 'Scheduled'
                            : 'Manual'}
                        </div>
                      </div>
                      <div className={classes.KVFlex}>
                        <div className={classes.flexLeftCol}>
                          Last Updated Time
                        </div>
                        <div style={DETAIL_VALUE_STYLE}>
                          {logTypeStatus?.last_modified_time != null &&
                          logTypeStatus?.last_modified_time > 0
                            ? dayjs
                                .utc(logTypeStatus.last_modified_time)
                                .local()
                                .format(YEAR_MONTH_DAY_TIME_FORMAT)
                            : '--'}
                        </div>
                      </div>
                      <div className={classes.KVFlex}>
                        <div className={classes.flexLeftCol}>Status</div>
                        <div style={DETAIL_VALUE_STYLE}>
                          {logTypeStatus?.status}
                        </div>
                      </div>

                      <div className={classes.KVFlex} />
                    </div>
                  </Card>
                )
              )}
            </AccordionDetails>
          </Accordion>
        )}

      {/* Custom Object Status, Events Availability History */}

      {hasSalesForceStatus && (
        <Accordion className={classes.statusInfoAccordion}>
          {renderAccordionSummary(
            'Custom Object Status',
            hasWarningStatus ? 'warning' : undefined
          )}
          <AccordionDetails>
            {Array.isArray(item?.custom_object_status) &&
              item.custom_object_status.map(
                (logTypeStatus: CustomObjectStatusItem) => {
                  const {
                    actionIconClass,
                    displayStatus,
                    isFailed,
                    statusClass
                  } = getCustomObjectStatusPresentation(logTypeStatus?.status);

                  return (
                    <Card
                      key={logTypeStatus.log_type}
                      variant="outlined"
                      sx={{ px: 2, py: 2, my: 1 }}
                    >
                      <div style={{ position: 'relative' }}>
                        <span style={EMPTY_RIGHT_BADGE_NUMERIC_STYLE} />
                        <div className={classes.KVFlex}>
                          <div className={classes.flexLeftCol}>
                            Custom Object
                          </div>
                          <div className={classes.flexRightCol}>
                            {logTypeStatus.log_type}
                          </div>
                        </div>
                        <div className={classes.KVFlex}>
                          <div className={classes.flexLeftCol}>
                            Last Updated Time
                          </div>
                          <div className={classes.flexRightCol}>
                            {dayjs
                              .utc(logTypeStatus?.last_modified_time)
                              .local()
                              .format(YEAR_MONTH_DAY_TIME_FORMAT)}
                          </div>
                        </div>
                        <div className={classes.KVFlex}>
                          <div className={classes.flexLeftCol}>Status</div>
                          <div
                            className={`${classes.flexRightCol} ${statusClass}`}
                          >
                            {displayStatus}
                          </div>
                        </div>
                        <div className={classes.KVFlex}>
                          <div className={classes.flexLeftCol}>Action</div>
                          <div className={classes.flexRightCol}>
                            <Button
                              variant="outlined"
                              onClick={() =>
                                handleResubmitCustomObject(
                                  item?.source_uid,
                                  logTypeStatus?.log_type
                                )
                              }
                              disabled={!isFailed}
                            >
                              {!resubmitCustomObjectMutation.isPending ? (
                                <FontAwesomeIcon
                                  id="info-icon"
                                  icon={faPowerOff}
                                  size="lg"
                                  className={actionIconClass}
                                />
                              ) : (
                                <CircularProgress color="secondary" size={10} />
                              )}
                            </Button>
                          </div>
                        </div>
                        <div className={classes.KVFlex} />
                      </div>
                    </Card>
                  );
                }
              )}
          </AccordionDetails>
        </Accordion>
      )}

      {Array.isArray(item?.events_availability_history) &&
        item?.events_availability_history &&
        item?.events_availability_history.length > 0 &&
        item?.source_type_id === SOURCE_TYPE.SALESFORCE && (
          <Accordion className={classes.statusInfoAccordion}>
            {renderAccordionSummary('Events Availability History')}
            <AccordionDetails>
              {item?.events_availability_history.map(
                (logTypeStatus: EventAvailabilityItem) => (
                  <Card
                    key={`events-availability-${logTypeStatus.event_time}-${logTypeStatus.status}`}
                    variant="outlined"
                    sx={{ px: 2, py: 2, my: 1 }}
                  >
                    <div style={{ position: 'relative' }}>
                      <span style={EMPTY_RIGHT_BADGE_STYLE} />
                      <div className={classes.KVFlex}>
                        <div className={classes.flexLeftCol}>Time</div>
                        <div style={DETAIL_VALUE_STYLE}>
                          {logTypeStatus?.event_time}
                        </div>
                      </div>
                      <div className={classes.KVFlex}>
                        <div className={classes.flexLeftCol}>Status</div>
                        <div style={DETAIL_VALUE_STYLE}>
                          {logTypeStatus?.status}
                        </div>
                      </div>
                      <div className={classes.KVFlex} />
                    </div>
                  </Card>
                )
              )}
            </AccordionDetails>
          </Accordion>
        )}
    </Card>
  );
};

export default SourceStatusCard;
